import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PlusIcon, TrashIcon, UserIcon, BuildingIcon, FileTextIcon, MailIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { logActivity, ActivityTypes, logActivityOnLinkedParents } from '../utils/activityLogger'
import { fetchServiceItemsGrouped, ServiceItemGrouped } from '../utils/serviceItems'
import { ServiceItemSelect } from '../components/service/ServiceItemSelect'
import { AddressAutocomplete } from '../components/AddressAutocomplete'
import { LeadCustomerSearchSelect } from '../components/LeadCustomerSearchSelect'
import { PropertyProjectSearchSelect } from '../components/PropertyProjectSearchSelect'
import { selectionFromIds } from '../lib/leadCustomerSearch'
import {
  buildProposalDefaultsFromRequest,
  projectsFromLeadRaw,
} from '../lib/mapProposalRequestToProposal'
import { buildProposalRequestSnapshot, resolveNavigationAfterProposalFromRequest } from '../lib/proposalRequestDisplay'
import { resolveOwnerContact, hasLinkedOwner } from '../lib/resolveOwnerContact'
import { resolveLeadProject, resolveProposalSiteAddress, type ProjectItem } from '../lib/propertyProjectSearch'
import { SERVICE_TYPE_OPTIONS, buildAutoProposalTitle, isDsnyServiceType } from '../lib/serviceTypeOptions'
import ConsistencyWarnings from '../components/agreement/ConsistencyWarnings'
import { checkConsistency, splitIssues, getDefaultTemplateTypeForServiceType, getDefaultContractTypeForServiceType } from '../utils/consistencyRules'
import { AgreementState, IssueTarget, DocumentData, BillingFrequency, BillingMilestone, PricingMode, ContractType } from '../types/agreement'
import ProposalValidityFields from '../components/agreement/ProposalValidityFields'
import BillingFrequencySelect from '../components/agreement/BillingFrequencySelect'
import BillingMethodSelect from '../components/agreement/BillingMethodSelect'
import { BillingDayOfMonthSelect } from '../components/BillingDayOfMonthSelect'
import MilestoneScheduleEditor from '../components/agreement/MilestoneScheduleEditor'
import { LineItemUnitTypeSelect } from '../components/agreement/LineItemUnitTypeSelect'
import { PricingPresentationFields } from '../components/agreement/PricingPresentationFields'
import { buildRateCardLineItems, hasRateCardSeed } from '../lib/rateCardLineItems'
import { defaultValidUntil } from '../lib/dateUtils'
import {
  defaultBillingFrequencyForSave,
  normalizeBillingFrequency,
  normalizeBillingMethod,
} from '../lib/billingFrequencyHelpers'
import {
  computeMilestoneAmounts,
  createEmptyMilestone,
  saveProposalMilestones,
} from '../lib/milestoneBilling'
import { buildDefaultDocumentData } from '../lib/proposalDocumentTemplates'
import DocumentDataEditor from '../components/agreement/DocumentDataEditor'
import { getDSNYFrequencies } from '../utils/dsnyHelpers'
import { findDsnyPackageItem, type DsnyPackageKind } from '../lib/contractServiceLineItems'
import { useDivision } from '../contexts/DivisionContext'
import { resolveOperationalDivisionId } from '../lib/operationalDivisions'

interface ProposalNote {
  timestamp: string
  note: string
}

interface Customer {
  id: string
  company_name?: string
  contact_first_name: string
  contact_last_name: string
}

interface Property {
  id: string
  name: string
  address: string
  customer_id: string
  property_type?: string
}

export default function ProposalCreate() {
  const navigate = useNavigate()
  const { divisions } = useDivision()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [loading, setLoading] = useState(false)

  const [activeTab, setActiveTab] = useState<'overview' | 'items' | 'billing' | 'notes'>('overview')
  const [isTitleManual, setIsTitleManual] = useState(false)
  const [dsnyFreqNotice, setDsnyFreqNotice] = useState<string>('')
  // DSNY/Hallway scheduling modal
  const [showDsnyModal, setShowDsnyModal] = useState<boolean>(false)
  const [dsnyModalLoading, setDsnyModalLoading] = useState<boolean>(false)
  const [dsnyModalError, setDsnyModalError] = useState<string>('')
  const [dsnyModalDetected, setDsnyModalDetected] = useState<{
    garbage: number
    recycling: number
    organics: number
    total: number
  } | null>(null)
  const [dsnyVisitsPerWeek, setDsnyVisitsPerWeek] = useState<number>(0)
  const [hallwayVisitsPerWeek, setHallwayVisitsPerWeek] = useState<number>(1)
  const [dsnyPackageKind, setDsnyPackageKind] = useState<DsnyPackageKind>('building_upkeep')

  const [customers, setCustomers] = useState<Customer[]>([])
  const [leads, setLeads] = useState<any[]>([])
  const [selectedLead, setSelectedLead] = useState<any>(null)
  const [selectedCustomerProperties, setSelectedCustomerProperties] = useState<Property[]>([])
  const [entityType, setEntityType] = useState<'customer' | 'lead'>('customer')

  const [proposalData, setProposalData] = useState({
    title: '',
    description: '',
    template_type: '', // Template type (maintenance, cleaning, etc.)
    service_type: '', // Service type for scheduling
    contract_type: '' as '' | ContractType, // blank by default - user must choose
    customer_id: '',
    lead_id: '',
    property_id: '',
    property_type: '',
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    total_amount: 0,
    subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    sales_tax_status: '', // 'taxable', 'non-tax', 'exempt', or '' for 'use owner default'
    payment_terms: 30,
    valid_until: defaultValidUntil(),
    terms_and_conditions: '',
    status: 'draft' as 'draft' | 'sent' | 'viewed' | 'approved' | 'rejected' | 'expired' | 'converted',
    // New fields for contract alignment
    start_date: '',
    end_date: '',
    billing_frequency: '' as '' | BillingFrequency,
    billing_day_of_month: 1,
    monthly_amount: 0,
    late_fee_percentage: 0,
    customer_notes: '', // Customer-facing notes
    dsny_integration: false,
    dsny_intent_message: '',
    // Phase 3 new fields
    billing_method: '' as '' | 'per_period' | 'divide_total' | 'actual_usage' | 'progress',
    payment_method: '' as '' | 'cash' | 'check' | 'ach' | 'credit_card',
    // Document generation
    scope_of_work: '',
    document_data: {} as DocumentData,
  })

  const [notes, setNotes] = useState<ProposalNote[]>([])
  const [newNote, setNewNote] = useState('')
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)

  interface ProposalLineItem {
    id: string
    service_item_id?: string
    description: string
    quantity: number
    is_variable: boolean // Checkbox for variable items (per hour, per unit, etc.)
    unit_type: string
    unit_price: number
    total_price: number
    sort_order: number
    // Interval/recurrence fields for scheduling
    recurrence_type: 'one_time' | 'daily' | 'weekly' | 'bi_weekly' | 'monthly' | 'quarterly' | 'annually' | 'custom'
    recurrence_interval: number // X days per week, X times per month, etc.
    interval_unit: 'days_per_week' | 'days_per_month' | 'every_x_weeks' | 'every_x_months' | ''
    service_type?: string
  }

  const [billingMilestones, setBillingMilestones] = useState<BillingMilestone[]>([])
  const [lineItems, setLineItems] = useState<ProposalLineItem[]>([])

  const [serviceItemsGrouped, setServiceItemsGrouped] = useState<ServiceItemGrouped[]>([])
  const [proposalRequest, setProposalRequest] = useState<any>(null)
  const [availableRequests, setAvailableRequests] = useState<any[]>([])
  const [showRequestSelectionModal, setShowRequestSelectionModal] = useState(false)
  const prefillingFromRequestRef = useRef(false)
  const ownerContactReadOnly = hasLinkedOwner(proposalData.customer_id, proposalData.lead_id)

  // Modal state for creating new property/project
  const [showCreatePropertyModal, setShowCreatePropertyModal] = useState(false)
  const [creatingProperty, setCreatingProperty] = useState(false)
  const [newPropertyData, setNewPropertyData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    property_type: '',
  })

  useEffect(() => {
    fetchCustomers()
    fetchLeads()
    fetchServiceItems()

    // Check if we're creating from a proposal request
    const requestId = searchParams.get('requestId')
    if (requestId) {
      fetchProposalRequest(requestId)
    }
  }, [searchParams])

  const fetchServiceItems = async () => {
    try {
      const items = await fetchServiceItemsGrouped()
      console.log('Fetched service items:', items)
      setServiceItemsGrouped(items)
      if (items.length === 0) {
        console.warn('No service items found. Check if service_items table has active items.')
      }
    } catch (error) {
      console.error('Error fetching service items in ProposalCreate:', error)
    }
  }

  const fetchLeads = async () => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('id, company_name, contact_first_name, contact_last_name')
        .in('stage', ['new', 'contacted', 'qualified', 'proposal_sent']) // Only show open leads (not won or lost)
        .order('company_name')
        .order('contact_first_name')

      if (error) throw error
      setLeads(data || [])
    } catch (error) {
      console.error('Error fetching leads:', error)
    }
  }

  const fetchProposalRequest = async (requestId: string) => {
    try {
      prefillingFromRequestRef.current = true

      const { data, error } = await supabase
        .from('proposals')
        .select('*')
        .eq('id', requestId)
        .eq('record_type', 'request')
        .single()

      if (error) throw error
      if (!data) return

      setProposalRequest(data)

      const { data: serviceCategories } = await supabase
        .from('service_categories')
        .select('id, name')
        .eq('is_active', true)

      let customerData: any = null
      let leadData: any = null
      let propertyData: any = null
      let projects: ProjectItem[] = []

      if (data.customer_id) {
        const { data: cust } = await supabase
          .from('customers')
          .select('id, company_name, contact_first_name, contact_last_name, email, phone')
          .eq('id', data.customer_id)
          .single()
        customerData = cust

        if (
          data.property_id &&
          typeof data.property_id === 'string' &&
          !String(data.property_id).startsWith('project-')
        ) {
          const { data: prop } = await supabase
            .from('properties')
            .select(
              'id, name, address, city, state, zip_code, property_type, site_contact_name, site_contact_email, site_contact_phone'
            )
            .eq('id', data.property_id)
            .single()
          propertyData = prop
        }

        setEntityType('customer')
        await fetchPropertiesForCustomer(data.customer_id)
      } else if (data.lead_id) {
        const { data: lead } = await supabase
          .from('leads')
          .select(
            'id, projects, email, phone, contacts, company_name, contact_first_name, contact_last_name'
          )
          .eq('id', data.lead_id)
          .single()
        leadData = lead
        projects = projectsFromLeadRaw(lead?.projects)
        setSelectedLead(lead)
        setEntityType('lead')
      }

      const defaults = buildProposalDefaultsFromRequest(
        data,
        serviceCategories || [],
        projects
      )

      const matchedProject = resolveLeadProject(
        projects,
        data.lead_project_id,
        data.property_address
      )

      const resolvedContact = resolveOwnerContact({
        customer: customerData,
        lead: leadData,
        property: propertyData,
        leadProject: matchedProject,
      })

      setIsTitleManual(false)
      if (defaults.notes.length > 0) {
        setNotes(defaults.notes)
      }

      setProposalData((prev) => ({
        ...prev,
        title: '',
        description: defaults.description,
        template_type: defaults.template_type,
        service_type: defaults.service_type,
        contract_type: defaults.contract_type || prev.contract_type,
        customer_id: defaults.customer_id,
        lead_id: defaults.lead_id,
        property_id: defaults.property_id || prev.property_id,
        property_type:
          defaults.property_type || resolvedContact.property_type || prev.property_type,
        start_date: defaults.start_date,
        end_date: defaults.end_date,
        customer_notes: defaults.customer_notes,
        company_name: resolvedContact.company_name,
        contact_name: resolvedContact.contact_name,
        contact_email: resolvedContact.contact_email,
        contact_phone: resolvedContact.contact_phone,
        dsny_integration: isDsnyServiceType(defaults.service_type),
        document_data: defaults.service_type
          ? buildDefaultDocumentData(defaults.service_type)
          : prev.document_data,
      }))
    } catch (error) {
      console.error('Error fetching proposal request:', error)
      setNotification({
        type: 'error',
        message: 'Error loading proposal request data',
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      prefillingFromRequestRef.current = false
    }
  }

  useEffect(() => {
    const customerId = searchParams.get('customer')
    const propertyId = searchParams.get('property')
    const leadId = searchParams.get('leadId')

    if (customerId) {
      setEntityType('customer')
      setProposalData((prev) => ({ ...prev, customer_id: customerId }))
    }
    if (propertyId) {
      setProposalData((prev) => ({ ...prev, property_id: propertyId }))
    }
    if (leadId) {
      setEntityType('lead')
      setProposalData((prev) => ({ ...prev, lead_id: leadId }))
    }
  }, [searchParams])

  useEffect(() => {
    if (entityType === 'customer' && proposalData.customer_id) {
      fetchPropertiesForCustomer(proposalData.customer_id)
      // Check for existing requests when customer is selected
      checkForExistingRequests(proposalData.customer_id, 'customer', proposalData.property_id)
    } else {
      setSelectedCustomerProperties([])
    }
  }, [proposalData.customer_id, entityType, proposalData.property_id])

  // Fetch lead and its projects when lead is selected
  useEffect(() => {
    if (entityType === 'lead' && proposalData.lead_id) {
      fetchLeadWithProjects(proposalData.lead_id)
      // Check for existing requests when lead is selected
      checkForExistingRequests(proposalData.lead_id, 'lead', proposalData.property_id)
    } else {
      setSelectedLead(null)
    }
  }, [proposalData.lead_id, entityType, proposalData.property_id])

  // Check for existing proposal requests
  const checkForExistingRequests = async (entityId: string, entityType: 'customer' | 'lead', propertyId?: string) => {
    // Don't check if we already have a requestId in URL (user explicitly selected a request)
    // or if we already have a proposalRequest set
    const requestId = searchParams.get('requestId')
    if (requestId || proposalRequest) {
      return
    }

    try {
      let query = supabase
        .from('proposals')
        .select('*')
        .eq('record_type', 'request')
        .neq('status', 'converted') // Only show non-converted requests
        .order('created_at', { ascending: false })

      if (entityType === 'customer') {
        query = query.eq('customer_id', entityId)
      } else {
        query = query.eq('lead_id', entityId)
      }

      // If property is selected, also filter by property
      if (propertyId && typeof propertyId === 'string' && !propertyId.startsWith('project-')) {
        query = query.eq('property_id', propertyId)
      }

      const { data, error } = await query

      if (error) {
        console.error('Error checking for existing requests:', error)
        return
      }

      if (data && data.length > 0) {
        setAvailableRequests(data)
        // Show modal if there are requests and we haven't shown it yet
        setShowRequestSelectionModal(true)
      } else {
        setAvailableRequests([])
      }
    } catch (error) {
      console.error('Error checking for existing requests:', error)
    }
  }

  const handleSelectRequest = (request: any) => {
    setProposalRequest(request)
    setShowRequestSelectionModal(false)
    fetchProposalRequest(request.id)
  }

  const handleCreateNewProposal = () => {
    setShowRequestSelectionModal(false)
    setProposalRequest(null)
  }

  // Match property_id from proposal request after projects are loaded
  useEffect(() => {
    console.log('Project matching useEffect triggered:', {
      hasProposalRequest: !!proposalRequest,
      entityType,
      hasSelectedLead: !!selectedLead,
      hasProjects: !!selectedLead?.projects,
      projectsCount: selectedLead?.projects?.length || 0,
      requestPropertyId: proposalRequest?.property_id,
      currentPropertyId: proposalData.property_id,
    })

    if (proposalRequest && entityType === 'lead' && selectedLead?.projects && selectedLead.projects.length > 0) {
      // Try to match the property_id from the request to a project
      const requestPropertyId = proposalRequest.property_id
      let matchedProjectId: string | null = null

      console.log('Attempting to match project:', {
        requestPropertyId,
        projects: selectedLead.projects.map((p: any, i: number) => ({
          index: i,
          id: p.id,
          address: p.address,
        })),
      })

      // Method 1: Try exact ID match
      if (requestPropertyId) {
        const exactMatch = selectedLead.projects.find((p: any) => {
          return p.id && p.id.toString() === requestPropertyId.toString()
        })
        if (exactMatch) {
          matchedProjectId = exactMatch.id || `project-${selectedLead.projects.indexOf(exactMatch)}`
          console.log('Method 1: Exact ID match found', matchedProjectId)
        }
      }

      // Method 2: Try index-based match (project-${index})
      if (!matchedProjectId && requestPropertyId && requestPropertyId.toString().startsWith('project-')) {
        const index = parseInt(requestPropertyId.toString().replace('project-', ''))
        if (!isNaN(index) && selectedLead.projects[index]) {
          matchedProjectId = selectedLead.projects[index].id || `project-${index}`
          console.log('Method 2: Index-based match found', matchedProjectId)
        }
      }

      // Method 3: Try matching by address (if property_address is stored in request)
      if (!matchedProjectId && proposalRequest.property_address) {
        const addressMatch = selectedLead.projects.find(
          (p: any) => p.address && p.address === proposalRequest.property_address
        )
        if (addressMatch) {
          matchedProjectId = addressMatch.id || `project-${selectedLead.projects.indexOf(addressMatch)}`
          console.log('Method 3: Address match found', matchedProjectId)
        }
      }

      // Method 4: If only one project, auto-select it
      if (!matchedProjectId && selectedLead.projects.length === 1) {
        matchedProjectId = selectedLead.projects[0].id || 'project-0'
        console.log('Method 4: Auto-selecting single project', matchedProjectId)
      }

      // Update property_id if we found a match
      if (matchedProjectId) {
        console.log('Project match result:', {
          matchedProjectId,
          currentPropertyId: proposalData.property_id,
          willUpdate: proposalData.property_id !== matchedProjectId,
        })

        if (proposalData.property_id !== matchedProjectId) {
          setProposalData((prev) => ({
            ...prev,
            property_id: matchedProjectId || '',
          }))
        }
      } else {
        console.warn('No project match found for request property_id:', requestPropertyId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalRequest, selectedLead, entityType])

  // Auto-generate proposal title from address/project and service type
  useEffect(() => {
    let address = ''

    if (entityType === 'customer' && proposalData.property_id) {
      const selectedProperty = selectedCustomerProperties.find(
        (p) => p.id === proposalData.property_id
      )
      if (selectedProperty?.address) {
        address = selectedProperty.address
      }
    } else if (entityType === 'lead' && proposalData.property_id && selectedLead?.projects) {
      let selectedProject: any = null

      if (selectedLead.projects.some((p: any) => p.id === proposalData.property_id)) {
        selectedProject = selectedLead.projects.find((p: any) => p.id === proposalData.property_id)
      } else if (proposalData.property_id.includes('project-')) {
        const projectIndex = parseInt(proposalData.property_id.replace('project-', ''))
        if (projectIndex >= 0 && selectedLead.projects[projectIndex]) {
          selectedProject = selectedLead.projects[projectIndex]
        }
      }

      if (selectedProject?.address) {
        address = selectedProject.address
      }
    }

    if (!address && proposalRequest?.property_address) {
      address = proposalRequest.property_address
    }

    if (!isTitleManual && proposalData.service_type) {
      const autoTitle = buildAutoProposalTitle(address, proposalData.service_type)
      if (autoTitle) {
        setProposalData((prev) => ({
          ...prev,
          title: autoTitle,
        }))
      }
    }
  }, [
    proposalData.property_id,
    proposalData.service_type,
    isTitleManual,
    entityType,
    selectedCustomerProperties,
    selectedLead,
    proposalRequest?.property_address,
  ])

  // Pre-populate company name, contact info, and property type when customer/lead is selected
  useEffect(() => {
    if (prefillingFromRequestRef.current) return

    if (entityType === 'customer' && proposalData.customer_id) {
      const selectedCustomer = customers.find((c) => c.id === proposalData.customer_id)
      if (selectedCustomer) {
        setProposalData((prev) => ({
          ...prev,
          company_name: selectedCustomer.company_name || '',
          contact_name: `${selectedCustomer.contact_first_name || ''} ${selectedCustomer.contact_last_name || ''}`.trim(),
        }))
        fetchCustomerContactInfo(proposalData.customer_id)
      }
    } else if (entityType === 'lead' && proposalData.lead_id && selectedLead) {
      const primaryContact = selectedLead.contacts && selectedLead.contacts[0] ? selectedLead.contacts[0] : null

      // Handle contact name
      let contactName = ''
      if (primaryContact) {
        if (primaryContact.first_name || primaryContact.last_name) {
          contactName = `${primaryContact.first_name || ''} ${primaryContact.last_name || ''}`.trim()
        } else if (primaryContact.name) {
          contactName = primaryContact.name
        }
      }
      if (!contactName && (selectedLead.contact_first_name || selectedLead.contact_last_name)) {
        contactName = `${selectedLead.contact_first_name || ''} ${selectedLead.contact_last_name || ''}`.trim()
      }

      setProposalData((prev) => ({
        ...prev,
        company_name: selectedLead.company_name || '',
        contact_name: contactName,
        contact_email: primaryContact?.email || selectedLead.email || '',
        contact_phone: primaryContact?.phone || selectedLead.phone || '',
      }))
    }
  }, [proposalData.customer_id, proposalData.lead_id, entityType, customers, selectedLead])

  // Pre-populate property_type when property/project is selected
  useEffect(() => {
    if (entityType === 'customer' && proposalData.property_id) {
      const selectedProperty = selectedCustomerProperties.find(
        (p) => p.id === proposalData.property_id
      )
      if (selectedProperty) {
        console.log('useEffect: Setting property_type from selectedProperty:', {
          property_id: proposalData.property_id,
          property_type: selectedProperty.property_type,
        })
        setProposalData((prev) => {
          // Only update if property_type is different to avoid unnecessary re-renders
          if (prev.property_type !== selectedProperty.property_type) {
            return {
              ...prev,
              property_type: selectedProperty.property_type || '',
            }
          }
          return prev
        })
      } else if (selectedCustomerProperties.length > 0) {
        // Properties are loaded but this property_id is not in the list
        console.warn('Property not found in selectedCustomerProperties:', {
          property_id: proposalData.property_id,
          availableProperties: selectedCustomerProperties.map((p) => p.id),
        })
      }
    } else if (entityType === 'lead' && proposalData.property_id && selectedLead?.projects) {
      let selectedProject: any = null

      if (selectedLead.projects.some((p: any) => p.id === proposalData.property_id)) {
        selectedProject = selectedLead.projects.find((p: any) => p.id === proposalData.property_id)
      } else if (proposalData.property_id.includes('project-')) {
        const projectIndex = parseInt(proposalData.property_id.replace('project-', ''))
        if (projectIndex >= 0 && selectedLead.projects[projectIndex]) {
          selectedProject = selectedLead.projects[projectIndex]
        }
      }

      if (selectedProject) {
        setProposalData((prev) => {
          if (prev.property_type !== selectedProject.type) {
            return {
              ...prev,
              property_type: selectedProject.type || '',
            }
          }
          return prev
        })
      }
    }
  }, [proposalData.property_id, entityType, selectedCustomerProperties, selectedLead])

  const fetchCustomerContactInfo = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('email, phone, contact_first_name, contact_last_name')
        .eq('id', customerId)
        .single()

      if (!error && data) {
        if (prefillingFromRequestRef.current) return
        setProposalData((prev) => ({
          ...prev,
          contact_email: data.email || '',
          contact_phone: data.phone || '',
          contact_name: `${data.contact_first_name || ''} ${data.contact_last_name || ''}`.trim(),
        }))
      }
    } catch (error) {
      console.error('Error fetching customer contact info:', error)
    }
  }

  const fetchLeadWithProjects = async (leadId: string) => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('id, projects, email, phone, contacts, company_name, contact_first_name, contact_last_name')
        .eq('id', leadId)
        .single()

      if (error) throw error
      setSelectedLead(data)

      // Pre-populate contact info from lead
      const primaryContact = data.contacts && data.contacts[0] ? data.contacts[0] : null

      // Handle contact name
      let contactName = ''
      if (primaryContact) {
        if (primaryContact.first_name || primaryContact.last_name) {
          contactName = `${primaryContact.first_name || ''} ${primaryContact.last_name || ''}`.trim()
        } else if (primaryContact.name) {
          contactName = primaryContact.name
        }
      }
      if (!contactName && (data.contact_first_name || data.contact_last_name)) {
        contactName = `${data.contact_first_name || ''} ${data.contact_last_name || ''}`.trim()
      }

      if (prefillingFromRequestRef.current) return

      setProposalData((prev) => ({
        ...prev,
        company_name: data.company_name || '',
        contact_name: contactName,
        contact_email: primaryContact?.email || data.email || '',
        contact_phone: primaryContact?.phone || data.phone || '',
      }))
    } catch (error) {
      console.error('Error fetching lead with projects:', error)
      setSelectedLead(null)
    }
  }

  // Handler to open create property/project modal
  const handleOpenCreateModal = () => {
    setNewPropertyData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      property_type: '',
    })
    setShowCreatePropertyModal(true)
  }

  // Handler to create new property (for customers)
  const handleCreateProperty = async () => {
    if (!proposalData.customer_id) {
      setNotification({
        type: 'error',
        message: 'Please select a customer first',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    if (!newPropertyData.name && !newPropertyData.address) {
      setNotification({
        type: 'error',
        message: 'Please enter at least a name or address',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    setCreatingProperty(true)
    try {
      const insertData: any = {
        customer_id: proposalData.customer_id,
        name: newPropertyData.name || newPropertyData.address || 'New Property',
        address: newPropertyData.address || null,
        city: newPropertyData.city || null,
        state: newPropertyData.state || null,
        zip_code: newPropertyData.zip || null,
        property_type: newPropertyData.property_type || null,
      }

      const { data: property, error } = await supabase
        .from('properties')
        .insert([insertData])
        .select()
        .single()

      if (error) throw error

      // Refresh properties list
      await fetchPropertiesForCustomer(proposalData.customer_id)

      // Auto-select the newly created property
      setProposalData((prev) => ({
        ...prev,
        property_id: property.id,
        property_type: property.property_type || '',
      }))

      // Close modal and reset form
      setShowCreatePropertyModal(false)
      setNewPropertyData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        property_type: '',
      })

      setNotification({
        type: 'success',
        message: 'Property created successfully!',
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error: any) {
      console.error('Error creating property:', error)
      setNotification({
        type: 'error',
        message: 'Error creating property: ' + (error.message || 'Unknown error'),
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setCreatingProperty(false)
    }
  }

  // Handler to create new project (for leads)
  const handleCreateProject = async () => {
    if (!proposalData.lead_id) {
      setNotification({
        type: 'error',
        message: 'Please select a lead first',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    if (!newPropertyData.address) {
      setNotification({
        type: 'error',
        message: 'Please enter an address for the project',
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    setCreatingProperty(true)
    try {
      // Fetch current lead data
      const { data: currentLead, error: fetchError } = await supabase
        .from('leads')
        .select('projects')
        .eq('id', proposalData.lead_id)
        .single()

      if (fetchError) throw fetchError

      // Create new project object
      const newProject = {
        id: `project-${Date.now()}`,
        address: newPropertyData.address,
        city: newPropertyData.city || '',
        state: newPropertyData.state || '',
        zip: newPropertyData.zip || '',
        type: newPropertyData.property_type || '',
      }

      // Add new project to projects array
      const updatedProjects = [...(currentLead.projects || []), newProject]

      // Update lead with new projects array
      const { error: updateError } = await supabase
        .from('leads')
        .update({ projects: updatedProjects })
        .eq('id', proposalData.lead_id)

      if (updateError) throw updateError

      // Refresh lead data
      await fetchLeadWithProjects(proposalData.lead_id)

      // Auto-select the newly created project
      setProposalData((prev) => ({
        ...prev,
        property_id: newProject.id,
        property_type: newProject.type || '',
      }))

      // Close modal and reset form
      setShowCreatePropertyModal(false)
      setNewPropertyData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        property_type: '',
      })

      setNotification({
        type: 'success',
        message: 'Project created successfully!',
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error: any) {
      console.error('Error creating project:', error)
      setNotification({
        type: 'error',
        message: 'Error creating project: ' + (error.message || 'Unknown error'),
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setCreatingProperty(false)
    }
  }

  // Update subtotal and total when line items or tax settings change
  useEffect(() => {
    // For variable quantity recurring items, use unit_price (total per month) instead of total_price (which is 0)
    const lineItemsTotal = lineItems.reduce((sum, item) => {
      // If item is variable quantity AND recurring, use unit_price (represents total per month)
      if (item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time') {
        return sum + item.unit_price
      }
      // Otherwise use total_price (for fixed quantity items or one-time variable items)
      return sum + item.total_price
    }, 0)

    // Tax calculation based on sales_tax_status
    // Only calculate tax if status is 'taxable', otherwise tax is 0
    const shouldCalculateTax = proposalData.sales_tax_status === 'taxable'
    const taxAmount = shouldCalculateTax ? lineItemsTotal * (proposalData.tax_rate / 100) : 0
    const total = lineItemsTotal + taxAmount

    setProposalData((prev) => ({
      ...prev,
      subtotal: lineItemsTotal,
      tax_amount: taxAmount,
      total_amount: total,
    }))
  }, [lineItems, proposalData.tax_rate, proposalData.sales_tax_status])

  // Line item management functions
  const handleLineItemChange = (index: number, field: string, value: string | number | boolean) => {
    const newLineItems = [...lineItems]
    newLineItems[index] = { ...newLineItems[index], [field]: value }

    // Recalculate total if quantity or unit_price changed (but not if is_variable is true)
    if ((field === 'quantity' || field === 'unit_price') && !newLineItems[index].is_variable) {
      newLineItems[index].total_price =
        Number(newLineItems[index].quantity) * Number(newLineItems[index].unit_price)
    } else if (field === 'is_variable') {
      // When toggling is_variable, update quantity and total accordingly
      if (value) {
        newLineItems[index].total_price = 0
        newLineItems[index].quantity = 0
      } else {
        newLineItems[index].total_price =
          Number(newLineItems[index].quantity) * Number(newLineItems[index].unit_price)
      }
    }

    setLineItems(newLineItems)
  }

  // Helper function to normalize unit_type from service catalog to line item unit_type
  const normalizeUnitType = (catalogUnitType: string): string => {
    const normalized = catalogUnitType.toLowerCase().trim()
    // Map common variations to standard unit_type values
    const unitTypeMap: Record<string, string> = {
      monthly: 'per_month',
      'per month': 'per_month',
      month: 'per_month',
      hourly: 'per_hour',
      'per hour': 'per_hour',
      hour: 'per_hour',
      sqft: 'per_sqft',
      'sq ft': 'per_sqft',
      'square foot': 'per_sqft',
      'square feet': 'per_sqft',
      visit: 'per_visit',
      'per visit': 'per_visit',
      unit: 'per_unit',
      'per unit': 'per_unit',
      flat: 'flat_rate',
      'flat rate': 'flat_rate',
    }
    return unitTypeMap[normalized] || catalogUnitType
  }

  const handleServiceItemSelect = (index: number, serviceItemId: string) => {
    if (!serviceItemId) {
      // Clear service item link
      const newLineItems = [...lineItems]
      newLineItems[index] = {
        ...newLineItems[index],
        service_item_id: undefined,
      }
      setLineItems(newLineItems)
      return
    }

    // Find the selected service item
    let selectedItem: { name: string; unit_type: string; base_price: number } | null = null
    for (const group of serviceItemsGrouped) {
      const item = group.items.find((i) => i.id === serviceItemId)
      if (item) {
        selectedItem = item
        break
      }
    }

    if (selectedItem) {
      const normalizedUnitType = normalizeUnitType(selectedItem.unit_type)
      const newLineItems = [...lineItems]
      newLineItems[index] = {
        ...newLineItems[index],
        service_item_id: serviceItemId,
        description: selectedItem.name, // Auto-populate description
        unit_type: normalizedUnitType, // Auto-populate unit_type (normalized to match dropdown options)
        unit_price: selectedItem.base_price, // Auto-populate unit_price
        total_price: Number(newLineItems[index].quantity) * Number(selectedItem.base_price), // Recalculate total
      }
      setLineItems(newLineItems)
    }
  }

  const addLineItem = () => {
    const newItem: ProposalLineItem = {
      id: `line_item_${Date.now()}`,
      service_item_id: undefined,
      description: '',
      quantity: 1,
      is_variable: false,
      unit_type: 'flat_rate',
      unit_price: 0,
      total_price: 0,
      sort_order: lineItems.length,
      // Default interval settings
      recurrence_type: proposalData.contract_type === 'recurring' ? 'weekly' : 'one_time',
      recurrence_interval: 1,
      interval_unit: '',
      service_type: proposalData.service_type || '',
    }
    setLineItems([...lineItems, newItem])
  }

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index))
  }

  const handlePricingModeChange = (mode: PricingMode) => {
    setProposalData((prev) => ({
      ...prev,
      document_data: { ...prev.document_data, pricingMode: mode },
      billing_method:
        mode === 'rate_card' && (!prev.billing_method || prev.billing_method === 'per_period')
          ? 'actual_usage'
          : prev.billing_method,
    }))
  }

  const mapRateCardToProposalLines = (serviceType: string) =>
    buildRateCardLineItems(serviceType).map((item) => ({
      ...item,
      service_item_id: undefined as string | undefined,
      recurrence_type: 'one_time' as const,
      recurrence_interval: 1,
      interval_unit: '' as const,
      service_type: serviceType || '',
    }))

  const handleSeedRateCard = () => {
    if (!hasRateCardSeed(proposalData.service_type)) return
    if (
      lineItems.length > 0 &&
      !window.confirm('Replace existing line items with rate card tiers?')
    ) {
      return
    }
    setLineItems(mapRateCardToProposalLines(proposalData.service_type))
  }

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, company_name, contact_first_name, contact_last_name')
        .in('status', ['onboarding', 'active']) // Only show open customers (onboarding or active, not terminated)
        .order('company_name')

      if (error) throw error
      setCustomers(data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
    }
  }

  const fetchPropertiesForCustomer = async (customerId: string) => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, name, address, customer_id, property_type')
        .eq('customer_id', customerId)
        .order('name')

      if (error) throw error
      setSelectedCustomerProperties(data || [])

      // If there's already a property_id selected, populate property_type now
      if (proposalData.property_id && data) {
        const selectedProperty = data.find((p) => p.id === proposalData.property_id)
        if (selectedProperty && selectedProperty.property_type) {
          console.log('fetchPropertiesForCustomer: Setting property_type after fetch:', {
            property_id: proposalData.property_id,
            property_type: selectedProperty.property_type,
          })
          setProposalData((prev) => ({
            ...prev,
            property_type: selectedProperty.property_type || '',
          }))
        }
      }
    } catch (error) {
      console.error('Error fetching properties:', error)
    }
  }

  const addNote = () => {
    if (newNote.trim()) {
      const timestamp = new Date().toISOString()
      setNotes([{ timestamp, note: newNote }, ...notes])
      setNewNote('')
    }
  }

  const removeNote = (index: number) => {
    setNotes(notes.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'Enter') {
      addNote()
    }
  }

  // Validation warnings for soft validation
  const [validationWarnings, setValidationWarnings] = useState<string[]>([])

  // Check for validation warnings (soft validation)
  const checkValidationWarnings = (): string[] => {
    const warnings: string[] = []

    if (!proposalData.start_date) {
      warnings.push('Start date is not set - will need to be set during conversion')
    }
    if (!proposalData.end_date) {
      warnings.push('End date is not set - will need to be set during conversion')
    }
    if (proposalData.contract_type === 'recurring' && lineItems.some(item => item.recurrence_type === 'one_time')) {
      warnings.push('Some line items are set as one-time but contract type is recurring')
    }
    if (proposalData.total_amount === 0 && lineItems.length > 0) {
      warnings.push('Total amount is $0 - please verify pricing')
    }

    return warnings
  }

  // Validation and navigation
  const validateTab = (tab: 'overview' | 'items' | 'billing' | 'notes'): boolean => {
    switch (tab) {
      case 'overview':
        return !!(
          proposalData.title &&
          proposalData.status &&
          proposalData.template_type &&
          proposalData.property_id &&
          ((entityType === 'customer' && proposalData.customer_id) ||
            (entityType === 'lead' && proposalData.lead_id))
        )
      case 'items':
        return true // Line items are optional
      case 'billing':
        return true // Billing details are optional
      case 'notes':
        return true // Notes are optional
      default:
        return false
    }
  }

  const handleNext = () => {
    if (!validateTab(activeTab)) {
      alert('Please fill in all required fields before continuing')
      return
    }

    const tabs: Array<'overview' | 'items' | 'billing' | 'notes'> = [
      'overview',
      'items',
      'billing',
      'notes',
    ]
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1])
    }
  }

  const handleBack = () => {
    const tabs: Array<'overview' | 'items' | 'billing' | 'notes'> = [
      'overview',
      'items',
      'billing',
      'notes',
    ]
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1])
    }
  }

  // Build the agreement state used by the consistency rules engine
  const agreementState: AgreementState = React.useMemo(() => {
    const siteAddress = resolveProposalSiteAddress({
      entityType,
      propertyId: proposalData.property_id,
      customerProperties: selectedCustomerProperties,
      leadProjects: selectedLead?.projects || [],
      fallbackAddress: proposalRequest?.property_address,
    })
    return {
      contract_type: (proposalData.contract_type as any) || '',
      service_type: proposalData.service_type as any,
      billing_frequency: proposalData.billing_frequency as any,
      billing_method: (proposalData as any).billing_method as any,
      payment_method: (proposalData as any).payment_method as any,
      template_type: proposalData.template_type as any,
      start_date: proposalData.start_date || null,
      end_date: proposalData.end_date || null,
      property_address: siteAddress,
      line_items: lineItems.map((li) => ({
        id: li.id,
        service_item_id: li.service_item_id,
        description: li.description,
        quantity: li.quantity,
        is_variable: li.is_variable,
        unit_type: li.unit_type,
        unit_price: li.unit_price,
        total_price: li.total_price,
        sort_order: li.sort_order,
        recurrence_type: li.recurrence_type as any,
        recurrence_interval: li.recurrence_interval,
        interval_unit: li.interval_unit as any,
        service_type: li.service_type as any,
      })),
      billing_milestones: billingMilestones,
      dsny_integration: proposalData.dsny_integration,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalData, lineItems, billingMilestones, selectedCustomerProperties, selectedLead, entityType, proposalRequest?.property_address])

  const consistencyIssues = React.useMemo(
    () => checkConsistency(agreementState, 'proposal'),
    [agreementState]
  )

  const handleNavigateIssue = (target: IssueTarget) => {
    if (!target) return
    // Map shared rules-engine tab names to this page's local tab names
    let local: 'overview' | 'items' | 'billing' | 'notes' = activeTab
    if (target.tab === 'overview') local = 'overview'
    else if (target.tab === 'services') local = 'items'
    else if (target.tab === 'billing') local = 'billing'
    else if (target.tab === 'notes') local = 'notes'
    if (local !== activeTab) setActiveTab(local)
  }

  // Returns the indices of existing line items that represent the bundled
  // DSNY package line (Building Upkeep or Exterior Maintenance).
  const findExistingDsnyLineIndexes = (): number[] =>
    lineItems
      .map((li, i) =>
        li.service_type === 'maintenance_dsny' ||
        li.service_type === 'dsny_prevailing_wage' ||
        li.service_type === 'dsny' ||
        /dsny|building\s*service|building\s*upkeep|exterior\s*maintenance|hallway/i.test(
          li.description || ''
        )
          ? i
          : -1
      )
      .filter((i) => i >= 0)

  // Opens the DSNY scheduling modal. Auto-fetches pickup frequency for the
  // selected property; user picks Building Upkeep vs Exterior and visit counts.
  const handleAutoDetectDSNYFrequencies = async () => {
    const siteAddress = resolveProposalSiteAddress({
      entityType,
      propertyId: proposalData.property_id,
      customerProperties: selectedCustomerProperties,
      leadProjects: selectedLead?.projects || [],
      fallbackAddress: proposalRequest?.property_address,
    })
    if (!siteAddress) {
      setDsnyFreqNotice('Please select a property with an address first.')
      return
    }
    setDsnyFreqNotice('')
    setDsnyModalError('')
    setDsnyModalDetected(null)

    const existingIdx = findExistingDsnyLineIndexes()[0]
    if (existingIdx !== undefined && existingIdx >= 0) {
      const existing = lineItems[existingIdx]
      const desc = existing.description || ''
      const isExterior = /exterior\s*maintenance/i.test(desc)
      setDsnyPackageKind(isExterior ? 'exterior_maintenance' : 'building_upkeep')
      const dsnyMatch = desc.match(/dsny[^0-9]*([0-9]+)\s*[x×]/i)
      const hallwayMatch = desc.match(/hallway[^0-9]*([0-9]+)\s*[x×]/i)
      const dsnyVal = dsnyMatch ? parseInt(dsnyMatch[1], 10) : 0
      const hallwayVal = hallwayMatch ? parseInt(hallwayMatch[1], 10) : 0
      setDsnyVisitsPerWeek(dsnyVal || existing.recurrence_interval || 0)
      setHallwayVisitsPerWeek(isExterior ? 0 : hallwayVal || 0)
    } else {
      setDsnyPackageKind('building_upkeep')
      setDsnyVisitsPerWeek(0)
      setHallwayVisitsPerWeek(1)
    }

    setShowDsnyModal(true)
    setDsnyModalLoading(true)
    try {
      const freq = await getDSNYFrequencies(siteAddress)
      if (!freq) {
        setDsnyModalError(
          'No DSNY data found for this address. You can still set custom visit frequencies below.'
        )
        return
      }
      setDsnyModalDetected({
        garbage: freq.garbage || 0,
        recycling: freq.recycling || 0,
        organics: freq.organics || 0,
        total: freq.total || 0,
      })
      if (existingIdx === undefined) {
        setDsnyVisitsPerWeek(freq.total || 0)
      }
    } catch (err) {
      console.error('DSNY frequency fetch failed:', err)
      setDsnyModalError(
        'Could not reach DSNY. You can still set custom visit frequencies below.'
      )
    } finally {
      setDsnyModalLoading(false)
    }
  }

  // Adds the bundled proposal line item from the DSNY modal.
  const confirmDsnyAdd = () => {
    const isExterior = dsnyPackageKind === 'exterior_maintenance'
    const dsny = Math.max(0, Number(dsnyVisitsPerWeek) || 0)
    const hallway = isExterior ? 0 : Math.max(0, Number(hallwayVisitsPerWeek) || 0)
    if (dsny === 0 && hallway === 0) {
      setDsnyModalError(
        isExterior
          ? 'Set at least one DSNY visit per week.'
          : 'Set at least one DSNY or hallway visit per week.'
      )
      return
    }
    try {
      const totalWeeklyVisits = dsny + hallway
      const existingIdxs = findExistingDsnyLineIndexes()
      const remainingItems =
        existingIdxs.length === 0
          ? lineItems
          : lineItems.filter((_, i) => !existingIdxs.includes(i))
      const replaced = existingIdxs.length > 0

      const catalogItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
      const defaultName = isExterior ? 'Exterior Maintenance' : 'Building Upkeep'

      const breakdownParts: string[] = []
      if (dsny > 0) {
        const detectedNote =
          dsnyModalDetected && dsnyModalDetected.total !== dsny
            ? ` (detected ${dsnyModalDetected.total})`
            : ''
        breakdownParts.push(`DSNY pickups ${dsny}×/wk${detectedNote}`)
      }
      if (hallway > 0) {
        breakdownParts.push(`hallway cleaning ${hallway}×/wk`)
      }
      const breakdown = breakdownParts.join(' + ')

      const unitPrice = catalogItem?.base_price || 0
      const baseName = catalogItem?.name || defaultName
      const fullDescription = breakdown ? `${baseName} — ${breakdown}` : baseName

      const newItem: ProposalLineItem = {
        id: `line_item_${Date.now()}_dsny_${Math.random().toString(36).slice(2, 7)}`,
        service_item_id: catalogItem?.id,
        description: fullDescription,
        quantity: 1,
        is_variable: false,
        unit_type: catalogItem?.unit_type || 'per_month',
        unit_price: unitPrice,
        total_price: unitPrice * 1,
        sort_order: remainingItems.length,
        recurrence_type: 'weekly',
        recurrence_interval: totalWeeklyVisits,
        interval_unit: 'days_per_week',
        service_type: isDsnyServiceType(proposalData.service_type)
          ? proposalData.service_type
          : 'maintenance_dsny',
      }

      setLineItems([...remainingItems, newItem])
      setProposalData((prev) => ({
        ...prev,
        contract_type:
          prev.contract_type === 'recurring'
            ? prev.contract_type
            : ('recurring' as 'one_time' | 'recurring' | ''),
        dsny_integration: dsny > 0 ? true : prev.dsny_integration,
        service_type:
          prev.service_type ||
          (isDsnyServiceType(proposalData.service_type)
            ? proposalData.service_type
            : 'maintenance_dsny'),
      }))

      const priceLabel = unitPrice
        ? `$${unitPrice}/${newItem.unit_type.replace('per_', '')}`
        : 'price TBD'
      const verb = replaced ? 'Updated' : 'Added'
      const splitNote = isExterior
        ? 'The contract will schedule DSNY pickup days (no hallway).'
        : 'The contract will split this into separate scheduled services (DSNY days and hallway day).'
      setDsnyFreqNotice(
        `${verb} "${fullDescription}" — ${totalWeeklyVisits}×/wk total (${priceLabel}). ${splitNote}`
      )
      setShowDsnyModal(false)
    } catch (err) {
      console.error('DSNY add failed:', err)
      setDsnyModalError('Could not add the line item. Please try again.')
    }
  }

  const handleSubmit = async () => {
    // Required-field backstop (HTML `required` + `*` markers are the primary UX;
    // these checks just guarantee we never submit with empty critical selectors).
    const missing: { tab: 'overview' | 'items' | 'billing' | 'notes'; message: string }[] = []
    if (!proposalData.contract_type) {
      missing.push({ tab: 'overview', message: 'Pick a job type (one-time or recurring).' })
    }
    if (!proposalData.billing_frequency) {
      missing.push({ tab: 'billing', message: 'Pick a billing frequency.' })
    }
    if (missing.length > 0) {
      const first = missing[0]
      setNotification({ type: 'error', message: first.message })
      setTimeout(() => setNotification(null), 6000)
      setActiveTab(first.tab)
      return
    }

    // Phase 4: enforce consistency-rule blockers before save
    const { blockers } = splitIssues(consistencyIssues)
    if (blockers.length > 0) {
      const first = blockers[0]
      setNotification({
        type: 'error',
        message: `Please fix ${blockers.length} issue${blockers.length === 1 ? '' : 's'} before saving. First: ${first.message}`,
      })
      setTimeout(() => setNotification(null), 6000)
      handleNavigateIssue(first.target)
      return
    }

    setLoading(true)

    try {
      // Generate proposal number
      const proposalNumber = `PROP-${Date.now()}`

      // Validate property_id - only use it if it's a valid UUID (for customers)
      // For leads with projects, property_id contains project identifier, not a UUID
      let propertyIdForDB = null
      if (entityType === 'customer' && proposalData.property_id) {
        // Check if it's a valid UUID (not starting with "project-")
        if (!proposalData.property_id.startsWith('project-')) {
          propertyIdForDB = proposalData.property_id
        }
      }

      // For leads, store project reference for later conversion
      let projectAddress = null
      let projectIndex = null
      if (entityType === 'lead' && proposalData.property_id && selectedLead?.projects) {
        // Extract project info for matching after conversion
        if (proposalData.property_id.includes('project-')) {
          projectIndex = parseInt(proposalData.property_id.replace('project-', ''))
          if (projectIndex >= 0 && selectedLead.projects[projectIndex]?.address) {
            projectAddress = selectedLead.projects[projectIndex].address
          }
        } else {
          // Try to find by project ID
          const project = selectedLead.projects.find((p: any) => p.id === proposalData.property_id)
          if (project?.address) {
            projectAddress = project.address
          }
        }
      }

      const proposalInsert: any = {
        proposal_number: proposalNumber,
        title: proposalData.title,
        customer_id:
          entityType === 'customer' && proposalData.customer_id ? proposalData.customer_id : null,
        lead_id: entityType === 'lead' && proposalData.lead_id ? proposalData.lead_id : null,
        property_id: propertyIdForDB,
        subtotal: proposalData.subtotal,
        tax_rate: proposalData.tax_rate,
        tax_amount: proposalData.tax_amount,
        sales_tax_status: proposalData.sales_tax_status || null,
        total: proposalData.total_amount,
        valid_until: proposalData.valid_until || null,
        status: proposalData.status,
        template_type: proposalData.template_type || null,
        notes: notes.length > 0 ? notes : null,
        // New fields for contract alignment
        contract_type: proposalData.contract_type || 'one_time',
        service_type: proposalData.service_type || null,
        operational_division_id: resolveOperationalDivisionId(
          proposalData.service_type,
          divisions
        ),
        start_date: proposalData.start_date || null,
        end_date: proposalData.end_date || null,
        billing_frequency: defaultBillingFrequencyForSave(
          proposalData.contract_type || 'one_time',
          proposalData.billing_frequency
        ),
        billing_day_of_month: Number((proposalData as any).billing_day_of_month) || 1,
        monthly_amount: proposalData.monthly_amount || null,
        late_fee_percentage: proposalData.late_fee_percentage || null,
        customer_notes: proposalData.customer_notes || null,
        dsny_integration: isDsnyServiceType(proposalData.service_type),
        dsny_intent_message: isDsnyServiceType(proposalData.service_type)
          ? 'Services will be scheduled based on DSNY pickup days for this property.'
          : null,
        // Phase 3: billing_method + payment_method (added by 20260303 migration)
        billing_method:
          proposalData.billing_frequency === 'progress'
            ? 'progress'
            : (proposalData as any).billing_method || null,
        payment_method: (proposalData as any).payment_method || null,
        // Document generation
        scope_of_work: proposalData.scope_of_work || null,
        document_data: proposalData.document_data || {},
      }

      // Add description if provided (column should exist after running migration)
      if (proposalData.description) {
        proposalInsert.description = proposalData.description
      }

      // Store project address for lead conversion mapping (if column exists)
      if (projectAddress) {
        proposalInsert.project_address = projectAddress
      }

      // Add payment_terms conditionally (may not exist in all schemas)
      if (proposalData.payment_terms) {
        proposalInsert.payment_terms = proposalData.payment_terms
      }

      // Add fields conditionally - only include if column exists and has a value
      // Some schemas use 'terms_and_conditions', others use 'terms_conditions'
      if (proposalData.terms_and_conditions && proposalData.terms_and_conditions.trim()) {
        proposalInsert.terms_and_conditions = proposalData.terms_and_conditions
      }

      // If we have a proposal request, UPDATE the existing record instead of creating new one
      // This maintains a single transaction ID and clear audit trail
      let proposal: any
      if (proposalRequest?.id) {
        // Update the existing request record to become a proposal
        proposalInsert.record_type = 'proposal'
        proposalInsert.converted_at = new Date().toISOString()
        proposalInsert.proposal_request_data = buildProposalRequestSnapshot(proposalRequest)
        // Keep request_number for reference, but use proposal_number as primary identifier
        if (!proposalInsert.proposal_number && proposalRequest.request_number) {
          proposalInsert.proposal_number = proposalRequest.request_number
        }

        const { data: updatedProposal, error: updateError } = await supabase
          .from('proposals')
          .update(proposalInsert)
          .eq('id', proposalRequest.id)
          .select()
          .single()

        if (updateError) throw updateError
        proposal = updatedProposal
        console.log('✅ Proposal request converted to proposal (same record):', proposalRequest.id)
      } else {
        // Create new proposal (no request to convert from)
        proposalInsert.record_type = 'proposal'
        const { data: newProposal, error: insertError } = await supabase
          .from('proposals')
          .insert(proposalInsert)
          .select()
          .single()

        if (insertError) throw insertError
        proposal = newProposal
      }

      if (proposal?.id) {
        if (proposalData.billing_frequency === 'progress') {
          await saveProposalMilestones(
            proposal.id,
            billingMilestones,
            proposalData.total_amount
          )
        } else {
          await saveProposalMilestones(proposal.id, [], proposalData.total_amount)
        }
      }

      // Update lead stage to 'proposal_sent' if proposal is sent and linked to a lead
      if (proposalData.status === 'sent' && proposalData.lead_id) {
        try {
          const { error: leadUpdateError } = await supabase
            .from('leads')
            .update({ stage: 'proposal_sent' })
            .eq('id', proposalData.lead_id)

          if (leadUpdateError) {
            console.error('Error updating lead stage:', leadUpdateError)
          } else {
            console.log('✅ Lead stage updated to proposal_sent:', proposalData.lead_id)
          }
        } catch (leadError) {
          console.error('Error updating lead stage:', leadError)
          // Don't fail proposal creation if lead update fails
        }
      }

      // Insert line items if any
      if (lineItems.length > 0 && lineItems.some((item) => item.description.trim())) {
        const lineItemsToInsert = lineItems
          .filter((item) => item.description.trim())
          .map((item) => ({
            proposal_id: proposal.id,
            service_item_id: item.service_item_id || null,
            description: item.description,
            quantity: item.is_variable ? 0 : item.quantity,
            is_variable: item.is_variable || false,
            unit_type: item.unit_type,
            unit_price: item.unit_price,
            total_price: item.is_variable ? 0 : item.total_price,
            sort_order: item.sort_order,
            // New interval fields
            recurrence_type: item.recurrence_type || 'one_time',
            recurrence_interval: item.recurrence_interval || 1,
            interval_unit: item.interval_unit || null,
            service_type: item.service_type || proposalData.service_type || null,
          }))

        if (lineItemsToInsert.length > 0) {
          const { error: lineItemsError } = await supabase
            .from('proposal_line_items')
            .insert(lineItemsToInsert)

          if (lineItemsError) throw lineItemsError
        }
      }

      // Log activity
      await logActivity({
        activity_type: ActivityTypes.PROPOSAL_CREATED,
        entity_type: 'proposal',
        entity_id: proposal.id,
        description: `Proposal "${proposalData.title}" created`,
        metadata: {
          customer_id: proposalData.customer_id,
          property_id: proposalData.property_id,
          total_amount: proposalData.total_amount,
        },
      })

      await logActivityOnLinkedParents({
        parents: {
          lead_id: proposalData.lead_id,
          customer_id: proposalData.customer_id,
          property_id: propertyIdForDB,
        },
        activity_type: ActivityTypes.PROPOSAL_CREATED,
        description: `Proposal "${proposalData.title}" created`,
        metadata: {
          child_entity_type: 'proposal',
          child_entity_id: proposal.id,
          proposal_number: proposal.proposal_number,
        },
      })

      setNotification({
        type: 'success',
        message: 'Proposal created successfully!',
      })
      setTimeout(() => {
        const convertedFromRequest = Boolean(proposalRequest?.id)
        navigate(
          resolveNavigationAfterProposalFromRequest(proposal.id, convertedFromRequest, returnTo),
          { replace: convertedFromRequest }
        )
      }, 1500)
    } catch (error) {
      console.error('Error creating proposal:', error)
      setNotification({
        type: 'error',
        message: 'Error creating proposal: ' + (error as any).message,
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setLoading(false)
    }
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
        <MobilePageHeader
          sticky
          title="Create Proposal"
          subtitle="Create a new proposal for a customer"
          onBack={() => {
            if (returnTo) {
              navigate(decodeURIComponent(returnTo))
            } else {
              navigate('/proposals')
            }
          }}
          backLabel={returnTo ? 'Back' : 'Back to Proposals'}
        />
        {proposalRequest && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <p className="font-medium">
              Creating proposal from Request{' '}
              {proposalRequest.request_number ? `#${proposalRequest.request_number}` : ''}
            </p>
            {proposalRequest.estimated_budget != null && (
              <p className="mt-1 text-blue-800">
                Estimated budget: ${Number(proposalRequest.estimated_budget).toLocaleString()}
              </p>
            )}
            {proposalRequest.urgency_level && proposalRequest.urgency_level !== 'normal' && (
              <p className="mt-1 text-blue-800">
                Urgency: {proposalRequest.urgency_level.replace(/_/g, ' ')}
              </p>
            )}
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/proposal-requests/view/${proposalRequest.id}?returnTo=${encodeURIComponent(
                    `/proposals/create?requestId=${proposalRequest.id}`
                  )}`
                )
              }
              className="mt-2 text-blue-700 underline hover:text-blue-900"
            >
              View original request
            </button>
          </div>
        )}

        <div className={`${mobile.surface} sticky top-[3.25rem] z-10 overflow-hidden`}>
          <div className="flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                activeTab === 'overview'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : activeTab === 'items' || activeTab === 'billing' || activeTab === 'notes'
                  ? 'text-blue-600'
                  : 'text-gray-400'
              }`}
            >
              Overview
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('items')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                activeTab === 'items'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : activeTab === 'billing' || activeTab === 'notes'
                  ? 'text-blue-600'
                  : 'text-gray-400'
              }`}
            >
              <span className="sm:hidden">Services</span>
              <span className="hidden sm:inline">Services & Schedule</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('billing')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                activeTab === 'billing'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : activeTab === 'notes'
                  ? 'text-blue-600'
                  : 'text-gray-400'
              }`}
            >
              <span className="sm:hidden">Billing</span>
              <span className="hidden sm:inline">Billing & Pricing</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('notes')}
              className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                activeTab === 'notes' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-400'
              }`}
            >
              <span className="sm:hidden">Notes</span>
              <span className="hidden sm:inline">Notes & Activity</span>
            </button>
          </div>
        </div>

        <div className={mobile.surface}>

          <div className="p-4 sm:p-6">
            {consistencyIssues.length > 0 && (
              <div className="mb-4">
                <ConsistencyWarnings
                  issues={consistencyIssues}
                  onNavigate={handleNavigateIssue}
                  currentTab={activeTab === 'items' ? 'services' : activeTab}
                />
              </div>
            )}
            {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Proposal Title and Status - At the top */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileTextIcon className="h-5 w-5" />
                      Proposal Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className={mobile.label}>
                          Proposal Title *
                        </label>
                        <div className="flex items-start gap-2">
                          <input
                            type="text"
                            required
                            readOnly={!isTitleManual}
                            value={proposalData.title}
                            onChange={(e) =>
                              setProposalData({ ...proposalData, title: e.target.value })
                            }
                            className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              !isTitleManual ? 'bg-gray-100 text-gray-700 cursor-default' : ''
                            }`}
                            placeholder="Auto-generated from property address and service type"
                          />
                          {!isTitleManual ? (
                            <button
                              type="button"
                              onClick={() => setIsTitleManual(true)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline shrink-0 pt-2"
                            >
                              Edit
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setIsTitleManual(false)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline shrink-0 pt-2"
                            >
                              Use auto-generated
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Generated from the property address and service type unless you edit
                          manually.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={mobile.label}>Status</label>
                          <select
                            value={proposalData.status}
                            onChange={(e) =>
                              setProposalData({
                                ...proposalData,
                                status: e.target.value as
                                  | 'draft'
                                  | 'sent'
                                  | 'viewed'
                                  | 'approved'
                                  | 'rejected'
                                  | 'expired',
                              })
                            }
                            className={mobile.field}
                          >
                            <option value="draft">Draft</option>
                            <option value="sent">Sent</option>
                            <option value="viewed">Viewed</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="expired">Expired</option>
                          </select>
                        </div>

                        <div>
                          <label className={mobile.label}>
                            Service Type *
                          </label>
                          <select
                            required
                            value={proposalData.service_type}
                            onChange={(e) => {
                              const newServiceType = e.target.value
                              const defaultTemplate = getDefaultTemplateTypeForServiceType(newServiceType)
                              const defaultContract = getDefaultContractTypeForServiceType(newServiceType)
                              const nextContractType = newServiceType
                                ? defaultContract || proposalData.contract_type
                                : ('' as '' | ContractType)
                              const newDocData = newServiceType
                                ? buildDefaultDocumentData(newServiceType)
                                : ({} as DocumentData)
                              setProposalData({
                                ...proposalData,
                                service_type: newServiceType,
                                template_type: newServiceType ? defaultTemplate : '',
                                contract_type: nextContractType,
                                billing_frequency: normalizeBillingFrequency(
                                  nextContractType,
                                  proposalData.billing_frequency
                                ),
                                billing_method:
                                  newDocData.pricingMode === 'rate_card'
                                    ? 'actual_usage'
                                    : proposalData.billing_method,
                                dsny_integration: isDsnyServiceType(newServiceType),
                                dsny_intent_message: isDsnyServiceType(newServiceType)
                                  ? 'Services will be scheduled based on DSNY pickup days for this property.'
                                  : '',
                                document_data: newDocData,
                              })
                              if (
                                newDocData.pricingMode === 'rate_card' &&
                                hasRateCardSeed(newServiceType)
                              ) {
                                setLineItems(mapRateCardToProposalLines(newServiceType))
                              }
                            }}
                            className={mobile.field}
                          >
                            <option value="">Select service type...</option>
                            {SERVICE_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Sets job type and template type automatically
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div data-consistency-anchor="contract-type">
                          <label className={mobile.label}>
                            Job Type *
                          </label>
                          <select
                            value={proposalData.contract_type}
                            onChange={(e) => {
                              const contract_type = e.target.value as '' | ContractType
                              setProposalData({
                                ...proposalData,
                                contract_type,
                                billing_frequency: normalizeBillingFrequency(
                                  contract_type,
                                  proposalData.billing_frequency
                                ),
                              })
                            }}
                            className={mobile.field}
                          >
                            <option value="">Select job type...</option>
                            <option value="one_time">One-Time Project</option>
                            <option value="as_needed">Ongoing (As Needed)</option>
                            <option value="recurring">Recurring Service</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            {proposalData.contract_type === 'recurring'
                              ? 'Recurring services will have interval scheduling options'
                              : proposalData.contract_type === 'as_needed'
                                ? 'Open-ended agreement — jobs created when needed, not on a fixed schedule'
                                : proposalData.contract_type === 'one_time'
                                  ? 'One-time project with a single service date'
                                  : 'Filled from service type, or choose manually.'}
                          </p>
                        </div>

                        {/* Template Type is auto-set from Service Type (hidden to avoid a confusing duplicate dropdown). */}
                        <div data-consistency-anchor="template-type" className="hidden" aria-hidden="true" />
                      </div>

                      <ProposalValidityFields
                        validUntil={proposalData.valid_until}
                        onValidUntilChange={(value) =>
                          setProposalData({ ...proposalData, valid_until: value })
                        }
                      />
                    </div>

                    {/* DSNY Intent Display */}
                    {isDsnyServiceType(proposalData.service_type) && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-blue-800">DSNY Integration</p>
                            <p className="text-sm text-blue-700 mt-1">
                              Services will be scheduled based on DSNY pickup days for this property.
                              The actual schedule will be fetched when this proposal is converted to a contract.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Two Column Layout: Customer/Property on left, Template/Contact on right */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column: Customer/Lead & Property/Project */}
                    <div className="space-y-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <UserIcon className="h-5 w-5" />
                          {entityType === 'customer' ? 'Customer & Property' : 'Lead & Project'}
                        </h3>
                        <div className="space-y-4">
                          <LeadCustomerSearchSelect
                            leads={leads}
                            customers={customers}
                            value={selectionFromIds(proposalData.lead_id, proposalData.customer_id)}
                            onChange={(selection) => {
                              if (!selection) {
                                setEntityType('customer')
                                setProposalData({
                                  ...proposalData,
                                  customer_id: '',
                                  lead_id: '',
                                  property_id: '',
                                })
                                setSelectedCustomerProperties([])
                                setSelectedLead(null)
                                return
                              }

                              setEntityType(selection.type)
                              if (selection.type === 'customer') {
                                setProposalData({
                                  ...proposalData,
                                  customer_id: selection.id,
                                  lead_id: '',
                                  property_id: '',
                                })
                                setSelectedCustomerProperties([])
                                setSelectedLead(null)
                              } else {
                                setProposalData({
                                  ...proposalData,
                                  lead_id: selection.id,
                                  customer_id: '',
                                  property_id: '',
                                })
                                setSelectedLead(null)
                                setSelectedCustomerProperties([])
                              }
                            }}
                            required
                            label="Send To"
                            placeholder="Type to search leads and customers..."
                          />

                          <PropertyProjectSearchSelect
                            mode={entityType === 'customer' ? 'property' : 'project'}
                            properties={selectedCustomerProperties}
                            projects={selectedLead?.projects || []}
                            value={proposalData.property_id}
                            onChange={(newPropertyId, option) => {
                              if (!option) {
                                setProposalData((prev) => ({
                                  ...prev,
                                  property_id: '',
                                  property_type: '',
                                }))
                                return
                              }

                              let newPropertyType = ''

                              if (entityType === 'customer') {
                                const property = option.raw as { property_type?: string }
                                newPropertyType = property.property_type || ''
                              } else {
                                const project = option.raw as { type?: string }
                                newPropertyType = project.type || ''
                              }

                              setProposalData((prev) => ({
                                ...prev,
                                property_id: newPropertyId,
                                property_type: newPropertyType,
                              }))
                            }}
                            onCreate={handleOpenCreateModal}
                            disabled={
                              entityType === 'customer'
                                ? !proposalData.customer_id
                                : !proposalData.lead_id
                            }
                            required
                            label={entityType === 'customer' ? 'Property' : 'Project'}
                            placeholder={
                              entityType === 'customer'
                                ? 'Type to search properties...'
                                : 'Type to search projects...'
                            }
                            id="proposal-create-property-project-search"
                          />
                          {entityType === 'lead' && selectedLead?.projects?.length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              No projects found for this lead
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <BuildingIcon className="h-5 w-5" />
                          Additional Information
                        </h3>

                        <div className="space-y-4">
                          <div>
                            <label className={mobile.label}>
                              Company Name
                            </label>
                            <input
                              type="text"
                              value={proposalData.company_name}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, company_name: e.target.value })
                              }
                              readOnly={ownerContactReadOnly}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                ownerContactReadOnly ? 'bg-gray-100 text-gray-700' : ''
                              }`}
                              placeholder="From linked customer or lead"
                            />
                          </div>

                          <div>
                            <label className={mobile.label}>
                              Property Type
                            </label>
                            <input
                              type="text"
                              value={proposalData.property_type}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, property_type: e.target.value })
                              }
                              className={mobile.field}
                              placeholder="Auto-populated from property/project"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column: Contact Information */}
                    <div className="space-y-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <MailIcon className="h-5 w-5" />
                          Contact Information
                        </h3>

                        <div className="space-y-4">
                          <div>
                            <label className={mobile.label}>
                              Contact Name
                            </label>
                            <input
                              type="text"
                              value={proposalData.contact_name}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, contact_name: e.target.value })
                              }
                              readOnly={ownerContactReadOnly}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                ownerContactReadOnly ? 'bg-gray-100 text-gray-700' : ''
                              }`}
                              placeholder="From linked customer or lead"
                            />
                          </div>

                          <div>
                            <label className={mobile.label}>
                              Contact Email
                            </label>
                            <input
                              type="email"
                              value={proposalData.contact_email}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, contact_email: e.target.value })
                              }
                              readOnly={ownerContactReadOnly}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                ownerContactReadOnly ? 'bg-gray-100 text-gray-700' : ''
                              }`}
                              placeholder="From linked customer or lead"
                            />
                          </div>

                          <div>
                            <label className={mobile.label}>
                              Contact Phone
                            </label>
                            <input
                              type="tel"
                              value={proposalData.contact_phone}
                              onChange={(e) =>
                                setProposalData({ ...proposalData, contact_phone: e.target.value })
                              }
                              readOnly={ownerContactReadOnly}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                ownerContactReadOnly ? 'bg-gray-100 text-gray-700' : ''
                              }`}
                              placeholder="From linked customer or lead"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Proposal Description - Below the two columns */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileTextIcon className="h-5 w-5" />
                      Proposal Description
                    </h3>

                    <div>
                      <label className={mobile.label}>
                        Description
                      </label>
                      <textarea
                        value={proposalData.description}
                        onChange={(e) =>
                          setProposalData({ ...proposalData, description: e.target.value })
                        }
                        rows={3}
                        className={mobile.field}
                        placeholder="Enter proposal description"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Line Items Tab */}
              {activeTab === 'items' && (
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleAutoDetectDSNYFrequencies}
                          className="flex items-center gap-2 px-4 py-2 border border-blue-500 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                          title="Add Building Upkeep (with hallway) or Exterior Maintenance (DSNY only)"
                        >
                          {findExistingDsnyLineIndexes().length > 0 ? 'Edit DSNY' : 'Add DSNY'}
                        </button>
                        <button
                          type="button"
                          onClick={addLineItem}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add Item
                        </button>
                      </div>
                    </div>
                    {dsnyFreqNotice && (
                      <div className="mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                        {dsnyFreqNotice}
                      </div>
                    )}

                    <PricingPresentationFields
                      serviceType={proposalData.service_type}
                      pricingMode={proposalData.document_data?.pricingMode}
                      onPricingModeChange={handlePricingModeChange}
                      onSeedRateCard={handleSeedRateCard}
                      lineItemCount={lineItems.length}
                    />

                    <div className="space-y-4">
                      {lineItems.map((item, index) => (
                        <div
                          key={item.id}
                          className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                        >
                          <div className="mb-4">
                            <label className={mobile.label}>
                              Link to Service Item{' '}
                              <span className="text-gray-400 font-normal">(Optional)</span>
                            </label>
                            <ServiceItemSelect
                              value={item.service_item_id || ''}
                              onChange={(id) => handleServiceItemSelect(index, id)}
                              grouped={serviceItemsGrouped}
                              allowEmpty
                              emptyValueLabel="None - Custom Entry"
                              onCatalogRefresh={fetchServiceItems}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Selecting a service item will auto-fill description, unit type, and
                              price (you can still edit)
                            </p>
                          </div>
                          {/* Interval Configuration Row - Only for recurring contracts */}
                          {proposalData.contract_type === 'recurring' && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div>
                                <label className={mobile.label}>
                                  Schedule Type
                                </label>
                                <select
                                  value={item.recurrence_type}
                                  onChange={(e) =>
                                    handleLineItemChange(index, 'recurrence_type', e.target.value)
                                  }
                                  className={mobile.field}
                                >
                                  <option value="one_time">One-Time</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="bi_weekly">Bi-Weekly</option>
                                  <option value="monthly">Monthly</option>
                                  <option value="quarterly">Quarterly</option>
                                  <option value="annually">Annually</option>
                                  <option value="custom">Custom</option>
                                </select>
                              </div>

                              {item.recurrence_type !== 'one_time' && (
                                <>
                                  <div>
                                    <label className={mobile.label}>
                                      Frequency
                                    </label>
                                    <div className="flex gap-2">
                                      <input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={item.recurrence_interval}
                                        onChange={(e) =>
                                          handleLineItemChange(index, 'recurrence_interval', parseInt(e.target.value) || 1)
                                        }
                                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      />
                                      <select
                                        value={item.interval_unit}
                                        onChange={(e) =>
                                          handleLineItemChange(index, 'interval_unit', e.target.value)
                                        }
                                        className={`${mobile.field} flex-1`}
                                      >
                                        <option value="">Select unit...</option>
                                        <option value="days_per_week">days per week</option>
                                        <option value="days_per_month">days per month</option>
                                        <option value="every_x_weeks">every X weeks</option>
                                        <option value="every_x_months">every X months</option>
                                      </select>
                                    </div>
                                  </div>

                                  <div>
                                    <label className={mobile.label}>
                                      Schedule Display
                                    </label>
                                    <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600">
                                      {item.interval_unit === 'days_per_week' && `${item.recurrence_interval} days per week`}
                                      {item.interval_unit === 'days_per_month' && `${item.recurrence_interval} days per month`}
                                      {item.interval_unit === 'every_x_weeks' && `Every ${item.recurrence_interval} week${item.recurrence_interval > 1 ? 's' : ''}`}
                                      {item.interval_unit === 'every_x_months' && `Every ${item.recurrence_interval} month${item.recurrence_interval > 1 ? 's' : ''}`}
                                      {!item.interval_unit && 'Set frequency above'}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                            <div className="md:col-span-2">
                              <label className={mobile.label}>
                                Description *
                              </label>
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) =>
                                  handleLineItemChange(index, 'description', e.target.value)
                                }
                                className={mobile.field}
                                placeholder="Item description"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2 relative pr-24">
                                Quantity
                                <div className="absolute right-0 -top-1 flex items-start gap-1.5">
                                  <input
                                    type="checkbox"
                                    id={`variable-${item.id}`}
                                    checked={item.is_variable}
                                    onChange={(e) =>
                                      handleLineItemChange(index, 'is_variable', e.target.checked)
                                    }
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                                  />
                                  <label
                                    htmlFor={`variable-${item.id}`}
                                    className="text-xs text-gray-600 font-normal leading-tight"
                                  >
                                    Variable
                                    <br />
                                    Quantity
                                  </label>
                                </div>
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    'quantity',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                disabled={item.is_variable}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  item.is_variable
                                    ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                                    : 'border-gray-300'
                                }`}
                                placeholder={item.is_variable ? 'Variable' : '0'}
                              />
                              {item.is_variable && (
                                <p className="mt-1 text-xs text-gray-500">Based on actual usage</p>
                              )}
                            </div>
                            <div>
                              <label className={mobile.label}>
                                Unit Type
                              </label>
                              <LineItemUnitTypeSelect
                                value={item.unit_type}
                                contractType={(proposalData.contract_type || 'one_time') as ContractType}
                                onChange={(value) =>
                                  handleLineItemChange(index, 'unit_type', value)
                                }
                              />
                            </div>
                            <div>
                              <label className={mobile.label}>
                                Unit Price
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    'unit_price',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className={mobile.field}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <label className={mobile.label}>
                                  Total
                                </label>
                                <div
                                  className={`px-3 py-2 rounded-lg border text-sm font-medium ${
                                    item.is_variable
                                      ? 'bg-orange-50 border-orange-300 text-orange-700'
                                      : 'bg-white border-gray-300'
                                  }`}
                                >
                                  {(() => {
                                    // For variable recurring items, show unit_price with billing interval
                                    if (item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time') {
                                      const frequencyLabels: Record<string, string> = {
                                        monthly: '/month',
                                        quarterly: '/quarter',
                                        annually: '/year',
                                        one_time: '',
                                      }
                                      const interval = frequencyLabels[proposalData.billing_frequency] || ''
                                      return `$${item.unit_price.toFixed(2)}${interval}`
                                    }
                                    // For variable one-time items, just show "Variable"
                                    if (item.is_variable) {
                                      return 'Variable'
                                    }
                                    // For fixed items, show total_price
                                    return `$${item.total_price.toFixed(2)}`
                                  })()}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeLineItem(index)}
                                className="p-2 text-red-600 hover:text-red-800 transition-colors mt-6"
                                title="Remove item"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {lineItems.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No line items added yet.</p>
                        <p className="text-xs mt-1">Click "Add Item" to get started.</p>
                      </div>
                    )}

                    {/* Line Items Total */}
                    {lineItems.length > 0 && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-gray-900">
                            {(() => {
                              // Check if there are variable quantity recurring items
                              const hasVariableRecurring = lineItems.some(
                                (item) => item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time'
                              )

                              if (hasVariableRecurring) {
                                // Show billing period based on billing_frequency
                                const frequencyLabels: Record<string, string> = {
                                  monthly: 'Monthly Subtotal',
                                  quarterly: 'Quarterly Subtotal',
                                  annually: 'Annual Subtotal',
                                  one_time: 'Line Items Subtotal',
                                }
                                return frequencyLabels[proposalData.billing_frequency] || 'Line Items Subtotal'
                              }
                              return 'Line Items Subtotal'
                            })()}:
                          </span>
                          <span className="text-2xl font-bold text-blue-600">
                            ${(() => {
                              // Use same calculation as in useEffect - for variable recurring items, use unit_price
                              return lineItems.reduce((sum, item) => {
                                if (item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time') {
                                  return sum + item.unit_price
                                }
                                return sum + item.total_price
                              }, 0).toFixed(2)
                            })()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Document generation settings */}
                  {proposalData.service_type && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mt-6">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Scope of Work (intro shown under Compensation)
                        </label>
                        <textarea
                          rows={3}
                          value={proposalData.scope_of_work}
                          onChange={(e) =>
                            setProposalData({ ...proposalData, scope_of_work: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Optional narrative describing the work to be performed"
                        />
                      </div>
                      <DocumentDataEditor
                        serviceType={proposalData.service_type}
                        phase="proposal"
                        value={proposalData.document_data}
                        onChange={(dd) => setProposalData({ ...proposalData, document_data: dd })}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Billing & Pricing Tab */}
              {activeTab === 'billing' && (
                <div className="space-y-6">
                  {/* Pricing Summary */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Summary</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Subtotal and Total are automatically calculated from the Services & Schedule tab.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className={mobile.label}>
                          Subtotal
                        </label>
                        <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-lg font-semibold text-gray-900">
                          ${proposalData.subtotal.toFixed(2)}
                        </div>
                      </div>

                      <div>
                        <label className={mobile.label}>
                          Tax Amount
                        </label>
                        <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-lg font-semibold text-gray-900">
                          ${proposalData.tax_amount.toFixed(2)}
                        </div>
                      </div>

                      <div>
                        <label className={mobile.label}>
                          {(() => {
                            // Check if there are variable quantity recurring items
                            const hasVariableRecurring = lineItems.some(
                              (item) => item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time'
                            )

                            if (hasVariableRecurring) {
                              // Show billing period based on billing_frequency
                              const frequencyLabels: Record<string, string> = {
                                monthly: 'Monthly Total',
                                quarterly: 'Quarterly Total',
                                annually: 'Annual Total',
                                one_time: 'Total Amount',
                              }
                              return frequencyLabels[proposalData.billing_frequency] || 'Total Amount'
                            }
                            return 'Total Amount'
                          })()}
                        </label>
                        <div className="px-3 py-2 bg-blue-100 border border-blue-300 rounded-lg text-xl font-bold text-blue-700">
                          ${proposalData.total_amount.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    {/* Monthly Amount Display - For recurring contracts */}
                    {proposalData.contract_type === 'recurring' && (
                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Monthly Payment
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Displayed to customer as monthly cost
                            </p>
                          </div>
                          <div className="text-right">
                            <input
                              type="number"
                              step="0.01"
                              value={proposalData.monthly_amount || (proposalData.billing_frequency === 'monthly' ? proposalData.total_amount : 0)}
                              onChange={(e) =>
                                setProposalData({
                                  ...proposalData,
                                  monthly_amount: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Billing Configuration */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Configuration</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div data-consistency-anchor="billing-frequency">
                        <label className={mobile.label}>
                          Billing Frequency *
                        </label>
                        <BillingFrequencySelect
                          value={proposalData.billing_frequency}
                          contractType={
                            proposalData.contract_type === 'recurring' ? 'recurring' : 'one_time'
                          }
                          onChange={(value) => {
                            const billing_method = normalizeBillingMethod(
                              value,
                              proposalData.billing_method
                            )
                            if (value === 'progress' && billingMilestones.length === 0) {
                              setBillingMilestones([createEmptyMilestone(0)])
                            }
                            setProposalData({
                              ...proposalData,
                              billing_frequency: value,
                              billing_method,
                            })
                          }}
                          className={mobile.field}
                        />
                      </div>

                      <BillingDayOfMonthSelect
                        billingFrequency={proposalData.billing_frequency}
                        value={(proposalData as any).billing_day_of_month ?? 1}
                        onChange={(day) =>
                          setProposalData({
                            ...proposalData,
                            billing_day_of_month: day,
                          } as typeof proposalData)
                        }
                      />

                      {proposalData.contract_type === 'recurring' &&
                        proposalData.billing_frequency &&
                        proposalData.billing_frequency !== 'one_time' &&
                        proposalData.billing_frequency !== 'after_each_service' &&
                        proposalData.billing_frequency !== 'progress' && (
                          <div data-consistency-anchor="billing-method">
                            <label className={mobile.label}>
                              Billing Method
                            </label>
                            <BillingMethodSelect
                              value={(proposalData as any).billing_method || ''}
                              billingFrequency={proposalData.billing_frequency}
                              onChange={(value) =>
                                setProposalData({
                                  ...proposalData,
                                  billing_method: value,
                                } as typeof proposalData)
                              }
                              className={mobile.field}
                            />
                          </div>
                        )}

                      {proposalData.billing_frequency === 'progress' && (
                        <div className="md:col-span-2" data-consistency-anchor="billing-method">
                          <label className={mobile.label}>
                            Billing Method
                          </label>
                          <BillingMethodSelect
                            value="progress"
                            billingFrequency="progress"
                            onChange={() => {}}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          />
                        </div>
                      )}

                      <div>
                        <label className={mobile.label}>
                          Payment Terms
                        </label>
                        <select
                          value={proposalData.payment_terms}
                          onChange={(e) =>
                            setProposalData({
                              ...proposalData,
                              payment_terms: parseInt(e.target.value) || 30,
                            })
                          }
                          className={mobile.field}
                        >
                          <option value={0}>Due on Receipt</option>
                          <option value={15}>Net 15</option>
                          <option value={30}>Net 30</option>
                          <option value={45}>Net 45</option>
                          <option value={60}>Net 60</option>
                        </select>
                      </div>

                      <div>
                        <label className={mobile.label}>
                          Sales Tax Status
                        </label>
                        <select
                          value={proposalData.sales_tax_status}
                          onChange={(e) =>
                            setProposalData({
                              ...proposalData,
                              sales_tax_status: e.target.value,
                            })
                          }
                          className={mobile.field}
                        >
                          <option value="">Use Owner Default</option>
                          <option value="taxable">Taxable</option>
                          <option value="non-tax">Non-Tax</option>
                          <option value="exempt">Exempt</option>
                        </select>
                      </div>

                      {proposalData.sales_tax_status === 'taxable' && (
                        <div>
                          <label className={mobile.label}>
                            Tax Rate (%)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={proposalData.tax_rate}
                            onChange={(e) => {
                              const rate = parseFloat(e.target.value) || 0
                              setProposalData({
                                ...proposalData,
                                tax_rate: rate,
                                tax_amount: (proposalData.subtotal * rate) / 100,
                                total_amount: proposalData.subtotal + (proposalData.subtotal * rate) / 100,
                              })
                            }}
                            className={mobile.field}
                            placeholder="0.00"
                          />
                        </div>
                      )}

                      <div>
                        <label className={mobile.label}>
                          Late Fee % <span className="text-gray-400">(Optional)</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={proposalData.late_fee_percentage || ''}
                          onChange={(e) =>
                            setProposalData({
                              ...proposalData,
                              late_fee_percentage: parseFloat(e.target.value) || 0,
                            })
                          }
                          className={mobile.field}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    {proposalData.billing_frequency === 'progress' && (
                      <MilestoneScheduleEditor
                        className="mt-4 p-4 bg-white border border-gray-200 rounded-lg"
                        milestones={billingMilestones}
                        totalAmount={proposalData.total_amount}
                        onChange={setBillingMilestones}
                      />
                    )}
                  </div>

                  {/* Optional Contract Dates */}
                  <div className="bg-gray-50 p-4 rounded-lg" data-consistency-anchor="contract-dates">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Contract Dates <span className="text-sm font-normal text-gray-500">(Optional)</span>
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      These dates can be set now or finalized during conversion to contract.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={mobile.label}>
                          Proposed Start Date
                        </label>
                        <input
                          type="date"
                          value={proposalData.start_date}
                          onChange={(e) =>
                            setProposalData({ ...proposalData, start_date: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>

                      <div>
                        <label className={mobile.label}>
                          Proposed End Date
                        </label>
                        <input
                          type="date"
                          value={proposalData.end_date}
                          onChange={(e) =>
                            setProposalData({ ...proposalData, end_date: e.target.value })
                          }
                          className={mobile.field}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Customer-Facing Notes */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Notes</h3>
                    <div>
                      <label className={mobile.label}>
                        Notes for Customer <span className="text-gray-400">(Visible on proposal)</span>
                      </label>
                      <textarea
                        value={proposalData.customer_notes}
                        onChange={(e) =>
                          setProposalData({ ...proposalData, customer_notes: e.target.value })
                        }
                        rows={3}
                        className={mobile.field}
                        placeholder="Any notes to include on the proposal for the customer..."
                      />
                    </div>
                  </div>

                  {/* Terms & Conditions */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Terms & Conditions</h3>
                    <textarea
                      value={proposalData.terms_and_conditions}
                      onChange={(e) =>
                        setProposalData({ ...proposalData, terms_and_conditions: e.target.value })
                      }
                      rows={6}
                      className={mobile.field}
                      placeholder="Enter terms and conditions"
                    />
                  </div>

                  {/* Validation Warnings */}
                  {checkValidationWarnings().length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">
                        Validation Warnings (Optional fields)
                      </h4>
                      <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                        {checkValidationWarnings().map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Notes & Activity Tab */}
              {activeTab === 'notes' && (
                <div className="space-y-4">
                  <div>
                    <label className={mobile.label}>Add Note</label>
                    <div className="flex gap-2">
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={3}
                        className={`${mobile.field} flex-1`}
                        placeholder="Enter a note (Ctrl+Enter to submit)"
                      />
                      <button
                        type="button"
                        onClick={addNote}
                        disabled={!newNote.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Press Ctrl+Enter to quickly submit</p>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-4">Notes History</h3>
                    {notes.length === 0 ? (
                      <p className="text-sm text-gray-500">No notes added yet</p>
                    ) : (
                      <div className="space-y-3">
                        {notes.map((note, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <p className="text-xs text-gray-500">
                                {new Date(note.timestamp).toLocaleString()}
                              </p>
                              <button
                                type="button"
                                onClick={() => removeNote(index)}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                Remove
                              </button>
                            </div>
                            <p className="text-sm text-gray-900">{note.note}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-4">Activity</h3>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-blue-700">
                        Activities will appear here after the proposal is created
                      </p>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>

          {/* Navigation Buttons */}
          <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-4 lg:rounded-xl lg:border lg:border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/proposals')}
              className={mobile.secondaryBtn}
            >
              Cancel
            </button>

            <div className="flex gap-2 sm:gap-3">
              {activeTab !== 'overview' && (
                <button
                  type="button"
                  onClick={handleBack}
                  className={mobile.secondaryBtn}
                >
                  Back
                </button>
              )}
              {activeTab === 'notes' ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className={mobile.primaryBtn}
                >
                  {loading ? 'Creating...' : (
                    <>
                      <span className="sm:hidden">Create</span>
                      <span className="hidden sm:inline">Create Proposal</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!validateTab(activeTab)}
                  className={mobile.primaryBtn}
                >
                  Next
                </button>
              )}
            </div>
          </div>

      {/* Notification Popup Modal */}
      {notification && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div
            className={`relative z-10 bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-md w-full mx-0 sm:mx-4 shadow-xl ${
              notification.type === 'success'
                ? 'border-2 border-green-500'
                : 'border-2 border-red-500'
            }`}
          >
            <div className="flex items-center gap-4">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification.type === 'success'
                    ? 'bg-green-100'
                    : 'bg-red-100'
                }`}
              >
                {notification.type === 'success' ? (
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-lg font-semibold ${
                    notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Property/Project Modal */}
      {showCreatePropertyModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div className="relative z-10 bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-md w-full mx-0 sm:mx-4 max-h-[90dvh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Create New {entityType === 'customer' ? 'Property' : 'Project'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreatePropertyModal(false)
                    setNewPropertyData({
                      name: '',
                      address: '',
                      city: '',
                      state: '',
                      zip: '',
                      property_type: '',
                    })
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {entityType === 'customer' && (
                  <div>
                    <label className={mobile.label}>
                      Property Name *
                    </label>
                    <input
                      type="text"
                      value={newPropertyData.name}
                      onChange={(e) =>
                        setNewPropertyData({ ...newPropertyData, name: e.target.value })
                      }
                      className={mobile.field}
                      placeholder="Enter property name"
                    />
                  </div>
                )}

                <div>
                  <label className={mobile.label}>
                    Address {entityType === 'lead' ? '*' : ''}
                  </label>
                  <AddressAutocomplete
                    value={newPropertyData.address}
                    onChange={(address) =>
                      setNewPropertyData({ ...newPropertyData, address })
                    }
                    onAddressSelect={(addressData) => {
                      setNewPropertyData({
                        ...newPropertyData,
                        address: addressData.address,
                        city: addressData.city,
                        state: addressData.state,
                        zip: addressData.zip,
                      })
                    }}
                    placeholder="Start typing address..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={mobile.label}>
                      City
                    </label>
                    <input
                      type="text"
                      value={newPropertyData.city}
                      onChange={(e) =>
                        setNewPropertyData({ ...newPropertyData, city: e.target.value })
                      }
                      className={mobile.field}
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <label className={mobile.label}>
                      State
                    </label>
                    <input
                      type="text"
                      value={newPropertyData.state}
                      onChange={(e) =>
                        setNewPropertyData({ ...newPropertyData, state: e.target.value })
                      }
                      className={mobile.field}
                      placeholder="State"
                    />
                  </div>
                </div>

                <div>
                  <label className={mobile.label}>
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={newPropertyData.zip}
                    onChange={(e) =>
                      setNewPropertyData({ ...newPropertyData, zip: e.target.value })
                    }
                    className={mobile.field}
                    placeholder="ZIP Code"
                  />
                </div>

                <div>
                  <label className={mobile.label}>
                    {entityType === 'customer' ? 'Property Type' : 'Project Type'}
                  </label>
                  <select
                    value={newPropertyData.property_type}
                    onChange={(e) =>
                      setNewPropertyData({ ...newPropertyData, property_type: e.target.value })
                    }
                    className={mobile.field}
                  >
                    <option value="">Select type...</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="cleaning">Cleaning</option>
                    <option value="landscaping">Landscaping</option>
                    <option value="security">Security</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreatePropertyModal(false)
                    setNewPropertyData({
                      name: '',
                      address: '',
                      city: '',
                      state: '',
                      zip: '',
                      property_type: '',
                    })
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={creatingProperty}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (entityType === 'customer') {
                      handleCreateProperty()
                    } else {
                      handleCreateProject()
                    }
                  }}
                  disabled={creatingProperty}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingProperty ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Request Selection Modal */}
      {showRequestSelectionModal && availableRequests.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Create Proposal from Request?
                </h2>
                <button
                  onClick={handleCreateNewProposal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                We found {availableRequests.length} existing proposal request{availableRequests.length > 1 ? 's' : ''} for this {entityType === 'customer' ? 'customer' : 'lead'}. Would you like to create a proposal from one of them?
              </p>

              <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                {availableRequests.map((request) => (
                  <div
                    key={request.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleSelectRequest(request)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">
                          {request.title || 'Untitled Request'}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {request.property_address || 'N/A'}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          {request.estimated_budget && (
                            <span>Budget: ${request.estimated_budget.toLocaleString()}</span>
                          )}
                          <span>
                            {new Date(request.created_at).toLocaleDateString()}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full ${
                              request.status === 'submitted'
                                ? 'bg-blue-100 text-blue-800'
                                : request.status === 'in_progress'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {request.status?.charAt(0).toUpperCase() + request.status?.slice(1) || 'Submitted'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSelectRequest(request)
                        }}
                        className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Use This Request
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCreateNewProposal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Create New Proposal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DSNY package scheduling modal */}
      {showDsnyModal && (() => {
        const existingDsnyIdxs = findExistingDsnyLineIndexes()
        const isReplacing = existingDsnyIdxs.length > 0
        const isExterior = dsnyPackageKind === 'exterior_maintenance'
        const previewName = isExterior ? 'Exterior Maintenance' : 'Building Upkeep'
        const effectiveHallway = isExterior ? 0 : hallwayVisitsPerWeek
        return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {isReplacing ? 'Edit DSNY package' : 'Add DSNY package'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Choose Building Upkeep (pickups + optional hallway) or Exterior Maintenance
                (pickups only). The proposal gets one line item; the contract expands the schedule.
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              {isReplacing && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  This will replace your existing DSNY package line on the proposal.
                </div>
              )}

              <div>
                <p className={mobile.label}>Package</p>
                <div className="flex flex-col gap-2 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="dsny_package_kind"
                      checked={dsnyPackageKind === 'building_upkeep'}
                      onChange={() => {
                        setDsnyPackageKind('building_upkeep')
                        if (hallwayVisitsPerWeek === 0) setHallwayVisitsPerWeek(1)
                      }}
                    />
                    Building Upkeep (DSNY + hallway)
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="dsny_package_kind"
                      checked={dsnyPackageKind === 'exterior_maintenance'}
                      onChange={() => {
                        setDsnyPackageKind('exterior_maintenance')
                        setHallwayVisitsPerWeek(0)
                      }}
                    />
                    Exterior Maintenance (DSNY only, no hallway)
                  </label>
                </div>
              </div>

              {dsnyModalLoading && (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                  Checking DSNY for this address...
                </div>
              )}
              {!dsnyModalLoading && dsnyModalDetected && (
                <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                  Detected at this address:{' '}
                  {[
                    dsnyModalDetected.garbage > 0
                      ? `garbage ${dsnyModalDetected.garbage}×/wk`
                      : null,
                    dsnyModalDetected.recycling > 0
                      ? `recycling ${dsnyModalDetected.recycling}×/wk`
                      : null,
                    dsnyModalDetected.organics > 0
                      ? `organics ${dsnyModalDetected.organics}×/wk`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(', ') || 'no pickups'}
                  . That&apos;s <strong>{dsnyModalDetected.total}</strong> unique pickup day
                  {dsnyModalDetected.total === 1 ? '' : 's'}/week.
                </div>
              )}
              {!dsnyModalLoading && dsnyModalError && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
                  {dsnyModalError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DSNY pickup visits per week
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={7}
                    value={dsnyVisitsPerWeek}
                    onChange={(e) => setDsnyVisitsPerWeek(parseInt(e.target.value, 10) || 0)}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-sm text-gray-500">times / week</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {dsnyModalDetected
                    ? 'Auto-filled from DSNY. Override if needed.'
                    : 'Set the number of DSNY pickup days per week.'}
                </p>
              </div>

              {!isExterior && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hallway cleaning visits per week
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={7}
                      value={hallwayVisitsPerWeek}
                      onChange={(e) => setHallwayVisitsPerWeek(parseInt(e.target.value, 10) || 0)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-sm text-gray-500">times / week</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Default is 1×/week. Set to 0 to skip hallway service.
                  </p>
                </div>
              )}

              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <strong>Preview:</strong> {previewName} —{' '}
                {[
                  dsnyVisitsPerWeek > 0 ? `DSNY ${dsnyVisitsPerWeek}×/wk` : null,
                  effectiveHallway > 0 ? `hallway ${effectiveHallway}×/wk` : null,
                ]
                  .filter(Boolean)
                  .join(' + ') || 'nothing selected'}
                {dsnyVisitsPerWeek + effectiveHallway > 0 && (
                  <span>
                    {' '}
                    (<strong>{dsnyVisitsPerWeek + effectiveHallway}</strong> total visits/week)
                  </span>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDsnyModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDsnyAdd}
                disabled={dsnyVisitsPerWeek + effectiveHallway === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isReplacing ? 'Save changes' : 'Add to proposal'}
              </button>
            </div>
          </div>
        </div>
        )
      })()}
    </MobilePage>
  )
}
